// Session utility functions
import { getCurrentUser } from "../features/auth/authApi";
import { signout } from "../features/auth/authSlice";

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - true if expired, false otherwise
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));

    // Check if token has expiration
    if (!payload.exp) return false; // No expiration means it doesn't expire

    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // If we can't parse, consider it expired
  }
};

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token
 * @returns {number|null} - expiration time in milliseconds or null
 */
export const getTokenExpiration = (token) => {
  if (!token) return null;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return null;

    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error("Error getting token expiration:", error);
    return null;
  }
};

/**
 * Validate session by checking token and verifying with backend
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<boolean>} - true if session is valid, false otherwise
 */
export const validateSession = async (dispatch) => {
  const token = localStorage.getItem("token");
  const user = localStorage.getItem("user");

  // If no token AND no user, session is definitely invalid
  if (!token && !user) {
    return false;
  }

  // If token exists, check if it's expired
  if (token && isTokenExpired(token)) {
    console.log("Token expired, clearing session");
    dispatch(signout());
    return false;
  }

  // If we have a user (even without token, i.e., OAuth session), verify with backend
  // Verification is the ultimate source of truth because it checks the session cookie too
  try {
    const currentUser = await getCurrentUser();
    if (!currentUser || currentUser.error) {
      console.log("Backend session invalid, clearing local session");
      dispatch(signout());
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error validating session:", error);
    // If it's a 401, clear the session
    if (error.response?.status === 401) {
      dispatch(signout());
      return false;
    }
    // For other errors, assume session is still valid (network issues)
    return true;
  }
};

/**
 * Set up automatic session expiration check
 * @param {Function} dispatch - Redux dispatch function
 * @param {number} checkInterval - Interval in milliseconds to check session (default: 5 minutes)
 */
export const setupSessionCheck = (dispatch, checkInterval = 5 * 60 * 1000) => {
  // Check immediately
  validateSession(dispatch);

  // Set up interval to check session periodically
  const intervalId = setInterval(() => {
    validateSession(dispatch);
  }, checkInterval);

  // Also check when page becomes visible (user comes back to tab)
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      validateSession(dispatch);
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Return cleanup function
  return () => {
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
};

